/**
 * chat_dock_react_wrap_controller.js — Stimulus shell for the chat dock Preact app.
 *
 * Mounts a DockApp Preact component into the dock body area. The dock shows
 * all rooms (no initial active room). When a room is clicked, a FloatPanel
 * is opened via the store.floatPanels signal.
 *
 * Each mount gets an independent store and cable lifecycle.
 *
 * ERB usage example:
 *   <div data-controller="chat-dock-react-wrap"
 *        data-chat-dock-react-wrap-person-id-value="<%= current_person&.id %>"
 *        data-chat-dock-react-wrap-tenant-id-value="<%= current_person&.tenant_id %>">
 *   </div>
 */

import { Controller } from "@hotwired/stimulus"
import { h, render }  from 'preact'
import { createChatStore }  from '../modules/chat/chat-store'
import { connectChatCable } from '../modules/chat/chat-cable'
import { chatApi }          from '../modules/chat/chat-api'
import { DockApp }          from '../modules/chat/components/DockApp'

export default class extends Controller {
  static values = {
    personId:       { type: Number, default: 0 },
    personName:     { type: String, default: '' },
    tenantId:       { type: String, default: '' },
    canManageRooms: { type: Boolean, default: false },
  }

  connect() {
    this.store = createChatStore({
      roomId:         null,  // Dock has no initial active room
      personId:       this.personIdValue,
      personName:     this.personNameValue,
      tenantId:       this.tenantIdValue,
      canManageRooms: this.canManageRoomsValue,
    })

    // Restore float panels from localStorage so previously open panels reappear
    // after a page refresh. The minimized state is preserved as well.
    if (this.personIdValue) {
      try {
        const key    = `tsh:chatDock:${this.personIdValue}.panels`
        const stored = localStorage.getItem(key)
        if (stored) {
          const parsed = JSON.parse(stored)
          if (Array.isArray(parsed) && parsed.length > 0) {
            this.store.floatPanels.value = parsed
          }
        }
      } catch { /* ignore */ }
    }

    this.cable = connectChatCable({
      store:    this.store,
      personId: this.personIdValue,
      tenantId: this.tenantIdValue,
    })

    // Open float panel after Turbo modal create from the dock
    this._onOpenRoom = (event) => {
      const roomId = event.detail?.roomId
      if (!roomId) return
      const already = this.store.floatPanels.value.some(p => p.roomId === roomId)
      if (!already) {
        this.store.floatPanels.value = [
          ...this.store.floatPanels.value,
          { roomId, minimized: false },
        ]
      } else {
        this.store.floatPanels.value = this.store.floatPanels.value.map(p =>
          p.roomId === roomId ? { ...p, minimized: false } : p
        )
      }
    }
    window.addEventListener('chat:open-room', this._onOpenRoom)

    render(
      h(DockApp, {
        store:    this.store,
        cable:    this.cable,
        personId: this.personIdValue,
        personName: this.personNameValue,
      }),
      this.element
    )
  }

  disconnect() {
    window.removeEventListener('chat:open-room', this._onOpenRoom)
    this.cable?.disconnect()
    render(null, this.element)
  }
}
